(() => {
  'use strict';

  const popupSelector = '.quick-buy-variant-popup';
  const variantCache = new Map();

  function money(value) {
    if (window.theme?.Currency?.formatMoney) {
      return window.theme.Currency.formatMoney(value, window.theme.settings.moneyFormat);
    }
    return (Number(value || 0) / 100).toFixed(2);
  }

  function getProductId(popup) {
    return popup.id.replace('variant-popup-', '');
  }

  function getCard(popup) {
    return popup.closest('.grid-product');
  }

  function getVariantsFromGlobal(productId) {
    const data = window.productVariantsData || {};
    return data[productId] || data[String(productId)] || [];
  }

  async function getVariants(popup) {
    const productId = getProductId(popup);
    const globalVariants = getVariantsFromGlobal(productId);
    if (globalVariants.length) return globalVariants;
    if (variantCache.has(productId)) return variantCache.get(productId);

    const card = getCard(popup);
    const handle = card?.querySelector('.grid-product__link')?.dataset.productHandle;
    if (!handle) return [];

    const request = fetch(`/products/${handle}.js`)
      .then(response => response.ok ? response.json() : null)
      .then(product => (product?.variants || []).map(variant => ({
        id: variant.id,
        option1: variant.option1,
        option2: variant.option2,
        available: variant.available,
        price: variant.price,
        featured_image: variant.featured_image || {}
      })))
      .catch(() => []);

    variantCache.set(productId, request);
    return request;
  }

  function getControls(popup) {
    return {
      colorInputs: [...popup.querySelectorAll('.color-options input.color-radio')],
      sizeInputs: [...popup.querySelectorAll('.size-options input.size-radio')],
      addButton: popup.querySelector('[data-quick-buy-add-to-cart]'),
      variantInput: popup.querySelector('[data-quick-buy-variant-id]'),
      price: popup.closest('.grid-product')?.querySelector('.grid-product__price--after')
    };
  }

  function setButton(popup, variant) {
    const { addButton, variantInput, price } = getControls(popup);
    if (!addButton || !variantInput) return;

    if (variant?.available) {
      variantInput.value = variant.id;
      addButton.disabled = false;
      addButton.dataset.variantId = variant.id;
      addButton.textContent = `${addButton.dataset.label || 'Add to cart'} ${money(variant.price)}`;
      if (price) price.textContent = money(variant.price);
    } else {
      variantInput.value = '';
      addButton.disabled = true;
      addButton.dataset.variantId = '';
      addButton.textContent = addButton.dataset.label || 'Add to cart';
    }
  }

  function updateActiveColor(popup, color) {
    if (!color) return;

    popup.querySelectorAll('.active-color-info .active-color, .byob-active-color-name .active-color').forEach(element => {
      element.textContent = color;
    });
  }

  function updateActiveSize(popup, size) {
    const activeSize = popup.querySelector('.active-size-info strong');
    if (activeSize) activeSize.textContent = size || '';
  }

  function updatePopupImageNavigation(popup) {
    const track = popup.querySelector('[data-quick-buy-image-track]');
    if (!track) return;

    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    const previousButton = popup.querySelector('[data-quick-buy-image-prev]');
    const nextButton = popup.querySelector('[data-quick-buy-image-next]');

    if (previousButton) previousButton.disabled = atStart;
    if (nextButton) nextButton.disabled = atEnd;
  }

  function updatePopupImage(popup, color, variant) {
    const track = popup.querySelector('[data-quick-buy-image-track]');
    const slides = [...(track?.querySelectorAll('[data-quick-buy-image-slide]') || [])];
    if (!track || !slides.length) return;

    const normalizeImageText = value => String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[–—]/g, '-')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const colorHandle = normalizeImageText(color);
    const availableColorHandles = getControls(popup).colorInputs
      .map(input => normalizeImageText(input.value))
      .filter(Boolean);
    const imageMatchesColor = value => {
      const normalizedValue = normalizeImageText(value);
      if (!normalizedValue || !colorHandle) return false;

      const paddedValue = `-${normalizedValue}-`;
      const matchingHandles = availableColorHandles
        .filter(handle => paddedValue.includes(`-${handle}-`))
        .sort((first, second) => second.length - first.length);

      return matchingHandles.length
        ? matchingHandles[0] === colorHandle
        : paddedValue.includes(`-${colorHandle}-`);
    };
    const matchingColorSlides = slides.filter(slide => {
      let filename = slide.dataset.imageSrc || '';
      try {
        filename = decodeURIComponent(new URL(filename, window.location.href).pathname.split('/').pop() || filename);
      } catch {
        filename = filename.split('/').pop() || filename;
      }
      const imageAlt = slide.dataset.imageAlt || slide.querySelector('img')?.alt || '';
      return imageMatchesColor(filename) || imageMatchesColor(imageAlt);
    });
    const visibleSlides = matchingColorSlides.length ? matchingColorSlides : slides;
    const featuredImage = variant?.featured_image;
    const imageId = String(featuredImage?.id || '');
    const imageSrc = typeof featuredImage === 'string' ? featuredImage : featuredImage?.src || '';
    const imagePath = source => {
      if (!source) return '';
      try {
        return new URL(source, window.location.origin).pathname;
      } catch {
        return String(source).split('?')[0];
      }
    };
    const featuredPath = imagePath(imageSrc);
    const selectedSlide = visibleSlides.find(slide =>
      (imageId && slide.dataset.mediaId === imageId)
      || (featuredPath && imagePath(slide.dataset.imageSrc) === featuredPath)
    );
    const orderedSlides = selectedSlide
      ? [selectedSlide, ...visibleSlides.filter(slide => slide !== selectedSlide)]
      : visibleSlides;

    slides.forEach(slide => {
      slide.hidden = !orderedSlides.includes(slide);
    });
    track.scrollTo({ left: 0, behavior: 'smooth' });
    requestAnimationFrame(() => updatePopupImageNavigation(popup));
  }

  function updateProductImage(popup, color) {
    if (!color) return;

    const card = popup.closest('.grid-product');
    const normalizeColor = value => String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[–—]/g, '-')
      .replace(/\s+/g, ' ');
    const normalizedColor = normalizeColor(color);
    const matchingSwatch = [...(card?.querySelectorAll('.grid-product__colors [data-product-card-swatch]') || [])]
      .find(swatch => {
        const swatchText = swatch.querySelector('.visually-hidden')?.textContent
          || swatch.nextElementSibling?.textContent
          || swatch.getAttribute('aria-label')?.split(' - ').pop()
          || '';
        return normalizeColor(swatchText) === normalizedColor;
      });
    if (matchingSwatch) matchingSwatch.click();
  }

  function updateColors(popup, variants) {
    const { colorInputs } = getControls(popup);
    const availableColors = [...new Set(variants.filter(variant => variant.available).map(variant => variant.option1))];

    colorInputs.forEach(input => {
      const item = input.closest('.variant-item');
      const isAvailable = availableColors.includes(input.value);
      if (item) item.style.display = isAvailable ? '' : 'none';
      input.disabled = !isAvailable;
    });

    const checked = colorInputs.find(input => input.checked && availableColors.includes(input.value));
    const firstAvailable = checked || colorInputs.find(input => availableColors.includes(input.value));
    colorInputs.forEach(input => { input.checked = input === firstAvailable; });

    if (firstAvailable) updateActiveColor(popup, firstAvailable.value);
  }

  function updateSizes(popup, variants, color) {
    const { sizeInputs } = getControls(popup);
    const colorVariants = variants.filter(variant => variant.option1 === color);
    const availableSizes = colorVariants.filter(variant => variant.available).map(variant => variant.option2);
    const existingSizes = colorVariants.map(variant => variant.option2);

    sizeInputs.forEach(input => {
      const item = input.closest('.variant-item');
      const label = item?.querySelector('.variant__button-label');
      const exists = existingSizes.includes(input.value);
      const available = availableSizes.includes(input.value);
      if (item) item.style.display = exists ? '' : 'none';
      if (label) label.classList.toggle('disabled', exists && !available);
      input.disabled = false;
      if (!exists || !available) {
        input.checked = false;
      }
    });

    const checked = sizeInputs.find(input => input.checked && availableSizes.includes(input.value));
    const firstAvailable = checked || sizeInputs.find(input => availableSizes.includes(input.value));
    if (firstAvailable) {
      sizeInputs.forEach(input => { input.checked = input === firstAvailable; });
    }

    const selectedSize = firstAvailable?.value;
    updateActiveSize(popup, selectedSize);
    const selectedVariant = variants.find(variant =>
      variant.option1 === color && variant.option2 === selectedSize
    );
    setButton(popup, selectedVariant);
    updatePopupImage(popup, color, selectedVariant);
    updateProductImage(popup, color);
  }

  async function syncPopup(popup) {
    const variants = await getVariants(popup);
    if (!variants.length) return;

    const { colorInputs } = getControls(popup);
    updateColors(popup, variants);
    const selectedColor = colorInputs.find(input => input.checked && !input.disabled)?.value || colorInputs.find(input => !input.disabled)?.value || variants.find(variant => variant.available)?.option1;
    if (!selectedColor) return;

    colorInputs.forEach(input => { input.checked = input.value === selectedColor; });
    updateActiveColor(popup, selectedColor);
    updateSizes(popup, variants, selectedColor);
  }

  function bindPopup(popup) {
    if (popup.dataset.quickBuyBound === 'true') return;
    popup.dataset.quickBuyBound = 'true';

    const addForm = popup.querySelector('[data-quick-buy-add-form]');
    const addButton = popup.querySelector('[data-quick-buy-add-to-cart]');
    const imageTrack = popup.querySelector('[data-quick-buy-image-track]');

    if (imageTrack) {
      imageTrack.addEventListener('scroll', () => updatePopupImageNavigation(popup), { passive: true });
      updatePopupImageNavigation(popup);
      if (typeof ResizeObserver === 'function') {
        const imageTrackObserver = new ResizeObserver(() => updatePopupImageNavigation(popup));
        imageTrackObserver.observe(imageTrack);
      }
    }

    if (addForm) {
      addForm.addEventListener('submit', async event => {
        event.preventDefault();

        const variantInput = addForm.querySelector('[data-quick-buy-variant-id]');
        const variantId = variantInput?.value;
        const quantityInput = addForm.querySelector('input[name="quantity"]');

        if (!variantId || !addButton || addButton.disabled) return;

        const cartUrl = window.theme?.routes?.cartAdd || '/cart/add.js';
        const formData = new URLSearchParams();
        formData.append('id', variantId);
        formData.append('quantity', quantityInput?.value || '1');

        addButton.disabled = true;
        addButton.classList.add('btn--loading');

        try {
          const response = await fetch(cartUrl, {
            method: 'POST',
            body: formData.toString(),
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          const result = response.headers.get('content-type')?.includes('application/json')
            ? await response.json()
            : null;

          if (!response.ok) {
            throw new Error(result?.description || 'Unable to add item to cart.');
          }

          if (typeof window.ShowCartNotification === 'function') {
            window.ShowCartNotification(result || {});
          }

          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: {
              product: result || { id: Number(variantId) },
              addToCartBtn: addButton,
              qtyparams: quantityInput?.value || '1'
            }
          }));

          if (typeof window.getCartItemCount === 'function') {
            window.getCartItemCount();
          }

          const productId = getProductId(popup);
          if (typeof window.closeVariantPopup === 'function') {
            window.closeVariantPopup(productId);
          } else {
            popup.style.display = 'none';
            popup.classList.remove('active-popup');
            document.getElementById('variant-popup-overlay')?.style.setProperty('display', 'none');
          }
        } catch (error) {
          console.error('Quick buy add to cart failed:', error);
        } finally {
          addButton.disabled = false;
          addButton.classList.remove('btn--loading');
        }
      });
    }

    popup.addEventListener('change', async event => {
      const input = event.target;
      if (!input.matches('.color-options input.color-radio, .size-options input.size-radio')) return;

      const variants = await getVariants(popup);
      if (!variants.length) return;

      if (input.matches('.color-radio')) {
        updateActiveColor(popup, input.value);
        updateSizes(popup, variants, input.value);
      } else {
        const color = popup.querySelector('.color-options input.color-radio:checked')?.value;
        updateActiveSize(popup, input.value);
        const variant = variants.find(item => item.option1 === color && item.option2 === input.value);
        setButton(popup, variant);
        updatePopupImage(popup, color, variant);
      }
    });

    syncPopup(popup);
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const navigationButton = event.target.closest('[data-quick-buy-image-prev], [data-quick-buy-image-next]');
    if (!navigationButton) return;

    const popup = navigationButton.closest(popupSelector);
    const track = popup?.querySelector('[data-quick-buy-image-track]');
    if (!track) return;

    event.preventDefault();
    event.stopPropagation();
    const direction = navigationButton.matches('[data-quick-buy-image-prev]') ? -1 : 1;
    const slides = [...track.querySelectorAll('[data-quick-buy-image-slide]:not([hidden])')];
    const targetSlide = direction > 0
      ? slides.find(slide => slide.offsetLeft > track.scrollLeft + 2)
      : slides.filter(slide => slide.offsetLeft < track.scrollLeft - 2).pop();

    if (targetSlide) track.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
  }, true);

  function bindAll(root = document) {
    root.querySelectorAll(popupSelector).forEach(bindPopup);
  }

  document.addEventListener('DOMContentLoaded', () => bindAll());
  document.addEventListener('byob:variants-ready', () => bindAll());
  document.addEventListener('page:loaded', () => bindAll());

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.matches?.(popupSelector)) bindPopup(node);
        node.querySelectorAll?.(popupSelector).forEach(bindPopup);
      });
    });
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
